import { useCallback, useEffect, useState } from 'react';

import { Button, Stack, Text, TextInput } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';

export interface RangeValue {
	min?: number;
	max?: number;
}

interface FiltersDrawerRangeSectionProps {
	/** Accordion section title */
	title: string;
	/** Description shown below the title */
	description: string;
	/** The currently committed range value */
	value: RangeValue;
	/** Called when the user clicks Apply to commit the range */
	onChange: (range: RangeValue) => void;
}

/**
 * Reusable numeric range filter section with local state and an "Apply" button.
 * The query only fires when the user explicitly commits, avoiding unnecessary
 * re-fetches on every keystroke.
 */
export function FiltersDrawerRangeSection({
	title,
	description,
	value,
	onChange,
}: FiltersDrawerRangeSectionProps): React.JSX.Element {
	const [localMin, setLocalMin] = useState(value.min?.toString() ?? '');
	const [localMax, setLocalMax] = useState(value.max?.toString() ?? '');

	// Sync local state when the committed value changes externally (e.g., clear all)
	useEffect(() => {
		setLocalMin(value.min?.toString() ?? '');
		setLocalMax(value.max?.toString() ?? '');
	}, [value.min, value.max]);

	const handleApply = useCallback((): void => {
		onChange({
			min: localMin ? Number(localMin) : undefined,
			max: localMax ? Number(localMax) : undefined,
		});
	}, [localMin, localMax, onChange]);

	const isDirty =
		(localMin || '') !== (value.min?.toString() ?? '') ||
		(localMax || '') !== (value.max?.toString() ?? '');
	const hasValue = localMin !== '' || localMax !== '';
	const showApply = isDirty && hasValue;

	return (
		<AccordionSection title={title}>
			<Stack itemsSpacing='12'>
				<Text size='bodySmall' color='secondary'>
					{description}
				</Text>
				<TextInput
					label='Min'
					placeholder='Min'
					type='number'
					size='small'
					value={localMin}
					onChange={setLocalMin}
				/>
				<TextInput
					label='Max'
					placeholder='Max'
					type='number'
					size='small'
					value={localMax}
					onChange={setLocalMax}
				/>
				{showApply && (
					<Button variant='secondary' size='small' onPress={handleApply}>
						Apply
					</Button>
				)}
			</Stack>
		</AccordionSection>
	);
}
